import { HttpClient, HttpContext } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { Bundle, DocumentReference, ServiceRequest } from 'fhir/r4';
import { Observable, catchError, map, of, switchMap } from 'rxjs';
import { backendEndPointToken } from '../../app.config';
import { LoadingUIEnabled, SkipHospitalSegregation } from '../../loading.interceptor';

@Injectable({ providedIn: 'root' })
export class ReferralViewerSourceService {
    private readonly http = inject(HttpClient);
    private readonly backendUrl = inject(backendEndPointToken);
    private readonly silentContext = new HttpContext()
        .set(LoadingUIEnabled, false)
        .set(SkipHospitalSegregation, true);

    loadBundleFromDocumentReferenceId(documentReferenceId: string): Observable<Bundle | null> {
        const normalizedId = documentReferenceId?.trim();
        if (!normalizedId) {
            return of(null);
        }

        return this.http.get<DocumentReference>(`${this.backendUrl}/DocumentReference/${normalizedId}`, {
            context: this.silentContext
        }).pipe(
            map(documentReference => this.decodeBundle(documentReference)),
            catchError(() => of(null))
        );
    }

    loadBundleForServiceRequestId(serviceRequestId: string): Observable<Bundle | null> {
        const normalizedId = serviceRequestId?.trim();
        if (!normalizedId) {
            return of(null);
        }

        return this.http.get<ServiceRequest>(`${this.backendUrl}/ServiceRequest/${normalizedId}`, {
            context: this.silentContext
        }).pipe(
            switchMap(serviceRequest => {
                const documentReferenceId = this.extractDocumentReferenceId(serviceRequest);
                if (!documentReferenceId) {
                    return of(null);
                }

                return this.loadBundleFromDocumentReferenceId(documentReferenceId);
            }),
            catchError(() => of(null))
        );
    }

    decodeBundle(documentReference: DocumentReference | null | undefined): Bundle | null {
        const attachment = documentReference?.content?.[0]?.attachment;
        if (!attachment) {
            return null;
        }

        if (attachment.data) {
            return this.decodeBundlePayload(attachment.data);
        }

        if (attachment.url?.startsWith('data:application/fhir+json;base64,')) {
            const encodedPayload = attachment.url.split(',', 2)[1] || '';
            return this.decodeBundlePayload(encodedPayload);
        }

        return null;
    }

    private extractDocumentReferenceId(serviceRequest: ServiceRequest): string | null {
        const references = Array.isArray(serviceRequest.supportingInfo) ? serviceRequest.supportingInfo : [];
        const documentReference = references.find(reference => reference.reference?.startsWith('DocumentReference/'));
        return documentReference?.reference?.split('/')[1] || null;
    }

    private decodeBundlePayload(encodedPayload: string): Bundle | null {
        try {
            const binary = atob(encodedPayload);
            const bytes = Uint8Array.from(binary, character => character.charCodeAt(0));
            const json = new TextDecoder().decode(bytes);
            const parsed = JSON.parse(json);

            return parsed?.resourceType === 'Bundle' ? parsed as Bundle : null;
        } catch {
            return null;
        }
    }
}